import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AiProviderPluginCatalogItem } from '@claude-code-api/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Upload,
  RefreshCw,
  Power,
  ShieldOff,
  Settings,
  CircleArrowUp,
} from 'lucide-react';
import { toast } from 'sonner';

function compareVersions(left: string, right: string): number {
  const parse = (value: string) =>
    value.split(/[.+-]/, 3).map((part) => Number(part) || 0);
  const a = parse(left);
  const b = parse(right);
  for (let index = 0; index < 3; index += 1) {
    if ((a[index] || 0) !== (b[index] || 0)) {
      return (a[index] || 0) - (b[index] || 0);
    }
  }
  return 0;
}

export default function AdminProviderPluginsPage() {
  const [plugins, setPlugins] = useState<AiProviderPluginCatalogItem[]>([]);
  const [publisherId, setPublisherId] = useState('');
  const [bundle, setBundle] = useState<File | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedPlugin, setSelectedPlugin] =
    useState<AiProviderPluginCatalogItem | null>(null);
  const latestPlugins = useMemo(() => {
    const latest = new Map<string, AiProviderPluginCatalogItem>();
    for (const plugin of plugins.filter(
      ({ status }) => status === 'approved' || status === 'active',
    )) {
      const current = latest.get(plugin.pluginId);
      if (!current || compareVersions(plugin.version, current.version) > 0) {
        latest.set(plugin.pluginId, plugin);
      }
    }
    return [...latest.values()];
  }, [plugins]);

  const updatePluginIds = useMemo(() => {
    const activeVersions = new Map<string, string>();
    for (const plugin of plugins) {
      if (plugin.status === 'active') {
        activeVersions.set(plugin.pluginId, plugin.version);
      }
    }
    return new Set(
      latestPlugins
        .filter((plugin) => {
          const activeVersion = activeVersions.get(plugin.pluginId);
          return (
            plugin.status === 'approved' &&
            activeVersion !== undefined &&
            compareVersions(plugin.version, activeVersion) > 0
          );
        })
        .map(({ pluginId }) => pluginId),
    );
  }, [latestPlugins, plugins]);

  const load = useCallback(async () => {
    const response = await fetch('/api/admin/provider-plugins', {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to load provider plugins');
    const payload = (await response.json()) as {
      data: AiProviderPluginCatalogItem[];
    };
    setPlugins(payload.data);
  }, []);

  useEffect(() => {
    void load().catch(() => toast.error('Could not load provider plugins'));
  }, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      const response = await fetch('/api/admin/provider-plugins/sync', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      await load();
      toast.success('Marketplace catalog synchronized');
    } catch {
      toast.error('Marketplace synchronization failed');
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!bundle || !publisherId) return;
    setBusy(true);
    try {
      const data = new FormData();
      data.append('file', bundle);
      const response = await fetch(
        `/api/admin/provider-plugins/upload?publisherId=${encodeURIComponent(publisherId)}`,
        { method: 'POST', credentials: 'include', body: data },
      );
      if (!response.ok) throw new Error();
      toast.success('Bundle submitted for build and scanning');
      setBundle(null);
      setPublisherId('');
      setUploadOpen(false);
    } catch {
      toast.error('Plugin upload failed');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (
    plugin: AiProviderPluginCatalogItem,
    status: 'active' | 'disabled' | 'revoked',
  ) => {
    const response = await fetch(
      `/api/admin/provider-plugins/${plugin.id}/status`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    );
    if (!response.ok) return toast.error('Plugin status update failed');
    await load();
    toast.success(`Plugin ${status}`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI provider plugins"
        description="Approved BSOL Marketplace adapters available to GIFT users."
      />
      <div className="flex items-center gap-2">
        <Button
          size="icon"
          variant="outline"
          onClick={sync}
          disabled={busy}
          title="Sync approved catalog"
          aria-label="Sync approved catalog"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
        </Button>
        <Button onClick={() => setUploadOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload plugin
        </Button>
      </div>
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload provider plugin</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="publisher-id">Publisher ID</Label>
              <Input
                id="publisher-id"
                value={publisherId}
                onChange={(event) => setPublisherId(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plugin-bundle">Signed plugin bundle</Label>
              <Input
                id="plugin-bundle"
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => setBundle(event.target.files?.[0] || null)}
              />
            </div>
            <Button onClick={upload} disabled={busy || !bundle || !publisherId}>
              <Upload className="mr-2 h-4 w-4" />
              Submit plugin
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="divide-y border-y">
        {latestPlugins.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No approved AI provider plugins are installed.
          </p>
        ) : (
          latestPlugins.map((plugin) => (
            <article
              key={plugin.id}
              className="grid gap-4 py-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
            >
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold">{plugin.name}</h2>
                  <Badge variant="outline">{plugin.version}</Badge>
                  <Badge variant="outline">
                    Contract {plugin.contractVersion}
                  </Badge>
                  <Badge>{plugin.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {plugin.description}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span>{plugin.publisher}</span>
                  <span>
                    {plugin.contractVersion === '2.0'
                      ? Object.keys(plugin.operations).join(', ')
                      : plugin.capabilities.modelFamilies.join(', ')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {plugin.capabilities.textInput && (
                    <Badge variant="secondary">Text</Badge>
                  )}
                  {plugin.capabilities.imageInput && (
                    <Badge variant="secondary">Vision</Badge>
                  )}
                  {plugin.capabilities.streaming && (
                    <Badge variant="secondary">Streaming</Badge>
                  )}
                  {plugin.capabilities.toolCalling && (
                    <Badge variant="secondary">Tools</Badge>
                  )}
                  {plugin.capabilities.structuredOutput && (
                    <Badge variant="secondary">JSON</Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  title="Inspect plugin contract"
                  onClick={() => setSelectedPlugin(plugin)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
                {plugin.status === 'approved' && (
                  <Button size="sm" onClick={() => setStatus(plugin, 'active')}>
                    {updatePluginIds.has(plugin.pluginId) ? (
                      <CircleArrowUp className="mr-2 h-4 w-4" />
                    ) : (
                      <Power className="mr-2 h-4 w-4" />
                    )}
                    {updatePluginIds.has(plugin.pluginId)
                      ? 'Update'
                      : 'Activate'}
                  </Button>
                )}
                {plugin.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus(plugin, 'disabled')}
                  >
                    Disable
                  </Button>
                )}
                {plugin.status !== 'revoked' && (
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Revoke plugin"
                    onClick={() => setStatus(plugin, 'revoked')}
                  >
                    <ShieldOff className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </article>
          ))
        )}
      </div>
      <Dialog
        open={selectedPlugin !== null}
        onOpenChange={(open) => !open && setSelectedPlugin(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPlugin?.name}</DialogTitle>
          </DialogHeader>
          {selectedPlugin && (
            <div className="grid gap-5 text-sm sm:grid-cols-2">
              <div>
                <h3 className="font-medium">Contract</h3>
                <p className="mt-1 text-muted-foreground">
                  Version {selectedPlugin.contractVersion}
                </p>
                <p className="text-muted-foreground">
                  Targets:{' '}
                  {selectedPlugin.targets.length
                    ? selectedPlugin.targets
                        .map((target) => `${target.product} ${target.version}`)
                        .join(', ')
                    : 'Legacy GIFT provider'}
                </p>
              </div>
              <div>
                <h3 className="font-medium">Permissions</h3>
                <p className="mt-1 text-muted-foreground">
                  Outbound hosts:{' '}
                  {selectedPlugin.permissions?.outboundHosts.join(', ') ||
                    'Provider-defined legacy access'}
                </p>
                <p className="text-muted-foreground">
                  Private network:{' '}
                  {selectedPlugin.permissions?.privateNetwork
                    ? 'Allowed'
                    : 'Denied'}
                </p>
              </div>
              <div className="sm:col-span-2">
                <h3 className="font-medium">Declared operations</h3>
                <div className="mt-2 divide-y border-y">
                  {Object.entries(selectedPlugin.operations).map(
                    ([name, operation]) => (
                      <div
                        key={name}
                        className="flex justify-between gap-4 py-2"
                      >
                        <span>{name}</span>
                        <span className="text-muted-foreground">
                          v{operation.version} ·{' '}
                          {operation.inputMedia.join(', ')} →{' '}
                          {operation.outputMedia.join(', ')}
                        </span>
                      </div>
                    ),
                  )}
                  {Object.keys(selectedPlugin.operations).length === 0 && (
                    <p className="py-2 text-muted-foreground">
                      Legacy v1 message provider
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
